import { milkSaleAmount } from '@dairy/domain';
import { recordIds, tryParseBirr, tryParseLiters, type SaleRecord, type PaymentRecord } from '@dairy/shared';

/** Distinguish managed daily records from existing sales, which must never be merged silently. */
export function customerDay(tenantId: string, date: string, buyerId: string, sales: SaleRecord[], payments: PaymentRecord[]) {
  const saleId = recordIds.dailyCustomerSale(tenantId, date, buyerId);
  const paymentId = recordIds.dailyCustomerPayment(tenantId, date, buyerId);
  const matching = sales.filter((s) => s.date === date && s.type === 'MILK' && s.buyerId === buyerId && !s.deletedAt);
  const managed = matching.find((s) => s.id === saleId);
  const historical = matching.filter((s) => s.id !== saleId);
  const linked = payments.filter((p) => p.saleId === saleId && !p.deletedAt);
  return { saleId, paymentId, managed, historical, payment: payments.find((p) => p.id === paymentId && !p.deletedAt), externalPaid: linked.filter((p) => p.id !== paymentId).reduce((sum, p) => sum + p.amountSantim, 0) };
}

/** Parse user-entered values using the application's exact integer-unit rules. */
export function calculateCustomerSale(liters: string, price: string, paid: string, externalPaid: number) {
  const quantity = tryParseLiters(liters);
  const unitPrice = tryParseBirr(price);
  const payment = tryParseBirr(paid);
  if (!quantity.ok || !unitPrice.ok || !payment.ok) return { ok: false as const };
  if (!quantity.ml && !liters.trim() && payment.santim) return { ok: true as const, ml: 0, priceSantim: unitPrice.santim ?? 0, amountSantim: 0, paidSantim: payment.santim, unpaidSantim: 0 };
  if (!quantity.ml || quantity.ml < 100 || quantity.ml > 2_000_000 || !unitPrice.santim || unitPrice.santim < 1) return { ok: false as const };
  const amount = milkSaleAmount(quantity.ml, unitPrice.santim);
  const paidSantim = payment.santim ?? 0;
  if (amount < 1) return { ok: false as const };
  return { ok: true as const, ml: quantity.ml, priceSantim: unitPrice.santim, amountSantim: amount, paidSantim, unpaidSantim: Math.max(0, amount - paidSantim - externalPaid) };
}

/** Replace the saved daily sale and its managed payment in the current buyer balance. */
export function projectedCustomerBalance(balance: number, savedSale: number, savedPayment: number, sale: number, payment: number) {
  return balance - savedSale + savedPayment + sale - payment;
}

